import { useCallback, useState } from 'react';
import {
  CommunicationOption,
  ContentProps,
} from 'models/profile-settings.model';
import {
  sendSettingsButtonClickedEvent,
  sendAccountSetupButtonClickedEvent,
} from 'utils/analytics-events';

export const useCommunicationModals = (
  options: CommunicationOption[],
  setOptions: (options: CommunicationOption[]) => void,
  setContents: (contents: ContentProps[]) => void,
  formatValue: (options: CommunicationOption[]) => ContentProps[],
  preEditOptions: CommunicationOption[],
  setPreEditOptions: (options: CommunicationOption[]) => void,
  setPendingOptions: (options: CommunicationOption[] | null) => void,
  isAccountSetup?: boolean,
) => {
  const [showPaperlessModal, setShowPaperlessModal] = useState(false);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [pendingPaperless, setPendingPaperless] = useState<boolean | null>(
    null,
  );
  const [pendingUnsubscribe, setPendingUnsubscribe] = useState<boolean | null>(
    null,
  );

  const handlePaperlessConfirm = useCallback(() => {
    if (pendingPaperless !== null) {
      const updatedOptions = options.map((opt) => {
        switch (opt.labelId) {
          case 'STR_PAPERLESS':
            return { ...opt, value: pendingPaperless };
          case 'STR_PERSONALIZED_EMAIL':
            return { ...opt, value: true };
          default:
            return opt;
        }
      });

      setOptions(updatedOptions);
      setContents(formatValue(updatedOptions));
      setPendingOptions(null);
    }

    setShowPaperlessModal(false);
    setPendingPaperless(null);
  }, [
    pendingPaperless,
    options,
    setOptions,
    setContents,
    formatValue,
    setPendingOptions,
  ]);

  const handlePaperlessCancel = useCallback(() => {
    setShowPaperlessModal(false);
    setPendingPaperless(null);
    setOptions(preEditOptions);
    setContents(formatValue(preEditOptions));
    setPendingOptions(null);
  }, [preEditOptions, setOptions, setContents, formatValue, setPendingOptions]);

  const handleUnsubscribeConfirm = useCallback(() => {
    if (pendingUnsubscribe !== null) {
      const updatedOptions = options.map((opt) => {
        switch (opt.labelId) {
          case 'STR_PERSONALIZED_EMAIL':
            return { ...opt, value: pendingUnsubscribe };
          case 'STR_PAPERLESS':
            return { ...opt, value: false };
          default:
            return opt;
        }
      });
      if (isAccountSetup) {
        sendAccountSetupButtonClickedEvent(
          'yes unsubscribe',
          true,
          'unsubscribe confirmation',
        );
      } else {
        sendSettingsButtonClickedEvent(
          'unsubscribe confirmation',
          'yes unsubscribe',
        );
      }
      setOptions(updatedOptions);
      setPreEditOptions(updatedOptions);
      setContents(formatValue(updatedOptions));
      setPendingOptions(null);
    }

    setShowUnsubscribeModal(false);
    setPendingUnsubscribe(null);
  }, [
    pendingUnsubscribe,
    options,
    setOptions,
    setPreEditOptions,
    setContents,
    formatValue,
    setPendingOptions,
    isAccountSetup,
  ]);

  const handleUnsubscribeCancel = useCallback(() => {
    setShowUnsubscribeModal(false);
    setPendingUnsubscribe(null);
    setPendingOptions(null);
  }, [setPendingOptions]);

  return {
    showPaperlessModal,
    setShowPaperlessModal,
    showUnsubscribeModal,
    setShowUnsubscribeModal,
    pendingPaperless,
    setPendingPaperless,
    pendingUnsubscribe,
    setPendingUnsubscribe,
    handlePaperlessConfirm,
    handlePaperlessCancel,
    handleUnsubscribeConfirm,
    handleUnsubscribeCancel,
  };
};
